// The calendar day an AWSDateTime stands for.
//
// The employee and benefit forms hand a date picker's value straight to the
// API, so a date is stored as local midnight serialised to UTC: 1 September
// picked in WIB arrives as "2026-08-31T17:00:00.000Z". Reading that in UTC puts
// the employee in August, and reading it in the browser's own zone makes every
// figure depend on where the page happens to be open. Shifting by 12 hours
// before taking the UTC date lands on the intended day for any zone from UTC-11
// to UTC+12 - WIB, WITA and WIT alike - and leaves a value already stored as UTC
// midnight on its own day.
//
// The payroll Lambda applies the same rule, so both engines put a date in the
// same month.
const HALF_DAY_MS = 12 * 60 * 60 * 1000;

const pad = (value) => String(value).padStart(2, "0");

// "YYYY-MM-DD", or null for a missing or unparseable value. Accepts an ISO
// string, a Date, a dayjs or a timestamp.
export const toCalendarDate = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const ms = new Date(
    typeof value === "object" && typeof value.valueOf === "function"
      ? value.valueOf()
      : value,
  ).valueOf();
  if (Number.isNaN(ms)) return null;

  const shifted = new Date(ms + HALF_DAY_MS);
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(
    shifted.getUTCDate(),
  )}`;
};

// { year, month } (month 1-12) of a value, or null.
export const toCalendarMonth = (value) => {
  const date = toCalendarDate(value);
  if (!date) return null;
  return { year: Number(date.slice(0, 4)), month: Number(date.slice(5, 7)) };
};

// "YYYY-MM" for a year and a 1-based month.
export const monthKey = (year, month) => `${year}-${pad(month)}`;

// First and last calendar day of a month, as "YYYY-MM-DD".
export const monthBounds = (year, month) => {
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return {
    start: `${monthKey(year, month)}-01`,
    end: `${monthKey(year, month)}-${pad(lastDay)}`,
  };
};

/** "YYYY-MM" for a Date, in the browser's calendar. */
export const toPeriod = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
};

/** A Date in the middle of a "YYYY-MM" month, safe from any time-zone shift. */
export const fromPeriod = (period) => {
  const [year, month] = period.split("-").map(Number);
  return new Date(year, month - 1, 15);
};

export const isPeriod = (value) =>
  typeof value === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(value);

/** The "YYYY-MM" `months` after `period`; negative counts go backwards. */
export const addMonths = (period, months) => {
  if (!isPeriod(period)) return null;
  const [year, month] = period.split("-").map(Number);
  const shifted = new Date(year, month - 1 + months, 1);
  return monthKey(shifted.getFullYear(), shifted.getMonth() + 1);
};
